/**
 * i18n resources for the Hotel Settings feature slice.
 *
 * Namespace: `settings`. Exposed as `{ namespace, resources: { en, bg, de, fr } }`
 * so the app shell can merge it into the shared i18next instance and tests can
 * pass it straight to `renderWithProviders({ i18nResources })`.
 *
 * Key parity is enforced by `@hostesa/i18n` (a missing key THROWS in test +
 * production), so every locale below carries the same key set. Non-`en` locales
 * are seeded as English placeholders where a real translation is not yet
 * available — the documented temporary state.
 */

const en = {
  eyebrow: 'Settings',
  title: 'Hotel settings',
  description: 'Voice, phone routing, and the people who can manage this hotel.',
  tabs: {
    voice: 'Voice',
    phone: 'Phone',
    members: 'Members',
  },
  voice: {
    title: 'Voice configuration',
    description: 'A read-only summary of how the AI speaks for this hotel.',
    fallbackLanguage: 'Fallback language',
    languages: 'Languages',
    handoffRules: 'Handoff rules',
    handoffRulesConfigured: '{{count}} rule(s) configured',
    handoffRulesNone: 'No handoff rules configured',
    manageScripts: 'Open Script Studio',
    manageHandoff: 'Edit handoff rules',
    loading: 'Loading voice configuration…',
    errorTitle: 'Could not load voice configuration',
    errorDescription: 'There was a problem loading the voice configuration. Please try again.',
  },
  phone: {
    title: 'Phone numbers',
    description: 'Forwarding rules and working hours for your provisioned numbers.',
    numberLabel: 'Number',
    statusLabel: 'Status',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    selectNumber: 'Select a number',
    loading: 'Loading phone numbers…',
    errorTitle: 'Could not load phone numbers',
    errorDescription: 'There was a problem loading your phone numbers. Please try again.',
    emptyTitle: 'No phone numbers yet',
    emptyDescription: 'No numbers have been provisioned for this hotel.',
  },
  forwarding: {
    title: 'Call forwarding',
    description: 'Forward calls to your reception or staff under the chosen condition.',
    conditionLabel: 'When',
    conditionBusy: 'Line is busy',
    conditionNoAnswer: 'No answer',
    conditionAfterHours: 'After hours',
    conditionAlways: 'Always',
    targetLabel: 'Forward to (phone)',
    targetPlaceholder: '+359888000000',
    targetInvalid: 'Enter a valid phone number in international format.',
    enabledLabel: 'Forwarding enabled',
    timeoutLabel: 'No-answer timeout (seconds)',
    timeoutPlaceholder: '20',
    timeoutInvalid: 'Enter a number between 0 and 120.',
    save: 'Save forwarding',
    saving: 'Saving…',
    saved: 'Forwarding saved.',
    saveError: 'Could not save forwarding. Please try again.',
  },
  workingHours: {
    title: 'Working hours',
    description: 'Set the open hours for each weekday. Calls outside these hours can be forwarded.',
    dayLabel: 'Day',
    days: {
      0: 'Sunday',
      1: 'Monday',
      2: 'Tuesday',
      3: 'Wednesday',
      4: 'Thursday',
      5: 'Friday',
      6: 'Saturday',
    },
    openLabel: 'Open this day',
    startLabel: 'Opens at',
    endLabel: 'Closes at',
    timeInvalid: 'Enter a time as HH:MM (24-hour).',
    rangeInvalid: 'The closing time must be after the opening time.',
    save: 'Save working hours',
    saving: 'Saving…',
    saved: 'Working hours saved.',
    saveError: 'Could not save working hours. Please try again.',
  },
  members: {
    title: 'Members',
    description: 'People who can sign in and manage this hotel.',
    nameHeading: 'Name',
    emailHeading: 'Email',
    roleHeading: 'Role',
    statusHeading: 'Status',
    actionsHeading: 'Actions',
    unnamed: 'Unnamed member',
    noEmail: 'No email',
    roleHotelAdmin: 'Hotel admin',
    roleHotelStaff: 'Hotel staff',
    roleTenantAdmin: 'Tenant admin',
    statusActive: 'Active',
    statusSuspended: 'Suspended',
    statusUnknown: 'Unknown',
    invite: 'Invite member',
    addMembership: 'Add membership',
    addMembershipFor: 'Grant another role to {{name}}',
    loading: 'Loading members…',
    errorTitle: 'Could not load members',
    errorDescription: 'There was a problem loading the members list. Please try again.',
    emptyTitle: 'No members yet',
    emptyDescription: 'Invite a colleague to help manage this hotel.',
    manageDisabledHint: 'You do not have permission to manage members for this hotel.',
  },
  invite: {
    title: 'Invite a member',
    description: 'Create a new account and grant it access to this hotel.',
    emailLabel: 'Email',
    emailPlaceholder: 'colleague@hotel.com',
    emailRequired: 'Email is required.',
    emailInvalid: 'Enter a valid email address.',
    fullNameLabel: 'Full name',
    fullNamePlaceholder: 'Jane Doe',
    passwordLabel: 'Temporary password',
    passwordPlaceholder: 'At least 8 characters',
    passwordRequired: 'A temporary password is required.',
    passwordTooShort: 'Use at least 8 characters.',
    roleLabel: 'Role',
    roleHotelAdmin: 'Hotel admin',
    roleHotelStaff: 'Hotel staff',
    submit: 'Send invite',
    submitting: 'Inviting…',
    success: 'Member invited.',
    error: 'Could not invite the member. Please try again.',
    cancel: 'Cancel',
  },
  addMembership: {
    title: 'Add a membership',
    description: 'Grant {{name}} another role on this hotel.',
    roleLabel: 'Role',
    roleHotelAdmin: 'Hotel admin',
    roleHotelStaff: 'Hotel staff',
    submit: 'Grant role',
    submitting: 'Granting…',
    success: 'Membership added.',
    error: 'Could not add the membership. Please try again.',
    cancel: 'Cancel',
  },
};

type SettingsResources = typeof en;

// Non-English locales reuse the English copy until translated (key parity).
const bg: SettingsResources = en;
const de: SettingsResources = en;
const fr: SettingsResources = en;

export const namespace = 'settings' as const;

export const resources: Record<'en' | 'bg' | 'de' | 'fr', { settings: SettingsResources }> = {
  en: { settings: en },
  bg: { settings: bg },
  de: { settings: de },
  fr: { settings: fr },
};

export const settingsI18n = { namespace, resources } as const;
